import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle } from 'lucide-react';

/**
 * KarpenterPoolFormModal — Phase C "Create / Edit / Delete NodePool" form.
 *
 * Modes:
 *   - "create": full form, POST /v1/clusters/{id}/karpenter/nodepools
 *   - "edit":   prefilled from `pool`, name field disabled, only changed
 *               fields go in the patch (computed by diffPayload below).
 *               PUT /v1/clusters/{id}/karpenter/nodepools/{name}
 *   - "delete": confirm dialog with type-the-name guard for force=true.
 *               DELETE /v1/clusters/{id}/karpenter/nodepools/{name}?force=…
 *
 * On success the parent's onSuccess({mode, pool_name, action_id}) callback
 * fires — KarpenterPanel optimistically flips the row to pending_approval.
 */

const CAPACITY_OPTIONS = ['spot', 'on-demand'];
const ARCH_OPTIONS = ['amd64', 'arm64'];
const CONSOLIDATION_POLICIES = ['WhenEmpty', 'WhenEmptyOrUnderutilized'];
const DEFAULT_CONSOLIDATE_AFTER = '1m';

// ─── Tiny chip-list editor: comma-separated text → []string ────────────────
const ChipsField = ({ label, hint, value, onChange, disabled }) => {
  const [text, setText] = useState((value || []).join(', '));
  useEffect(() => setText((value || []).join(', ')), [value]);
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {label}
        {hint && <span className="text-gray-400 ml-2 font-normal">{hint}</span>}
      </label>
      <input
        type="text"
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        onBlur={() =>
          onChange(text.split(',').map((s) => s.trim()).filter(Boolean))
        }
        className="w-full text-sm px-2 py-1.5 border rounded disabled:bg-gray-100"
      />
    </div>
  );
};

// ─── Disruption sub-form ──────────────────────────────────────────────────
const DisruptionFields = ({ value, onChange, disabled }) => {
  const v = value || {
    consolidation_policy: 'WhenEmptyOrUnderutilized',
    consolidate_after: DEFAULT_CONSOLIDATE_AFTER,
    budgets: [],
  };
  const update = (patch) => onChange({ ...v, ...patch });
  const updateBudget = (i, patch) => {
    const next = [...(v.budgets || [])];
    next[i] = { ...next[i], ...patch };
    update({ budgets: next });
  };
  return (
    <div className="space-y-2 border rounded p-2 bg-gray-50">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Policy</label>
          <select
            value={v.consolidation_policy}
            disabled={disabled}
            onChange={(e) => update({ consolidation_policy: e.target.value })}
            className="w-full text-sm px-2 py-1.5 border rounded"
          >
            {CONSOLIDATION_POLICIES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Consolidate After</label>
          <input
            type="text"
            value={v.consolidate_after}
            disabled={disabled}
            onChange={(e) => update({ consolidate_after: e.target.value })}
            className="w-full text-sm px-2 py-1.5 border rounded"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-700">Budgets</label>
          <Button
            size="sm" variant="ghost"
            disabled={disabled}
            onClick={() =>
              update({ budgets: [...(v.budgets || []), { nodes: '10%' }] })
            }
          >
            + Add
          </Button>
        </div>
        {(v.budgets || []).map((b, i) => (
          <div key={i} className="flex gap-1 items-center mb-1">
            <input
              placeholder="nodes (e.g. 10% or 3)"
              value={b.nodes || ''}
              disabled={disabled}
              onChange={(e) => updateBudget(i, { nodes: e.target.value })}
              className="flex-1 text-xs px-2 py-1 border rounded"
            />
            <input
              placeholder="schedule (cron)"
              value={b.schedule || ''}
              disabled={disabled}
              onChange={(e) => updateBudget(i, { schedule: e.target.value })}
              className="flex-1 text-xs px-2 py-1 border rounded"
            />
            <input
              placeholder="duration"
              value={b.duration || ''}
              disabled={disabled}
              onChange={(e) => updateBudget(i, { duration: e.target.value })}
              className="w-24 text-xs px-2 py-1 border rounded"
            />
            <Button
              size="sm" variant="ghost"
              disabled={disabled}
              onClick={() =>
                update({ budgets: v.budgets.filter((_, j) => j !== i) })
              }
            >
              ✕
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Edit-mode diff helper ────────────────────────────────────────────────
//
// For edit mode we send only the fields that actually changed, so the
// agent's UpdateNodePool patch path is minimal. Compares each form field
// against the original pool's projected value; equal/empty → omit.
const diffPayload = (form, original) => {
  const out = {};
  const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const fields = [
    'instance_families', 'instance_categories', 'instance_sizes',
    'capacity_types', 'architectures', 'zones', 'labels', 'taints',
    'limits_cpu', 'limits_memory', 'weight', 'disruption',
  ];
  for (const f of fields) {
    if (!same(form[f], original[f])) out[f] = form[f];
  }
  return out;
};

// ─── Build the original-shape projection for prefill ──────────────────────
const projectPoolToForm = (pool) => {
  if (!pool) return null;
  const fams = pool.instance_families || [];
  const cats = pool.instance_categories || [];
  return {
    name: pool.name || '',
    instance_families: fams,
    instance_categories: cats,
    instance_sizes: pool.instance_sizes || [],
    capacity_types: pool.capacity_types ||
      (pool.lifecycle === 'mixed' ? ['spot', 'on-demand']
        : pool.lifecycle ? [pool.lifecycle] : []),
    architectures: pool.architectures || ['amd64'],
    zones: pool.zones || [],
    labels: pool.labels || {},
    taints: pool.taints || [],
    limits_cpu: pool.limits_cpu || '',
    limits_memory: pool.limits_mem || pool.limits_memory || '',
    weight: pool.weight ?? null,
    disruption: pool.disruption ? {
      consolidation_policy: pool.disruption.consolidation_policy
        || pool.disruption.consolidationPolicy || 'WhenEmptyOrUnderutilized',
      consolidate_after: pool.disruption.consolidate_after
        || pool.disruption.consolidateAfter || DEFAULT_CONSOLIDATE_AFTER,
      budgets: pool.disruption.budgets || [],
    } : null,
    node_class_ref: pool.node_class_ref || pool.nodeClassRef || null,
  };
};

// ─── Main modal ──────────────────────────────────────────────────────────
const KarpenterPoolFormModal = ({
  open,
  mode,           // 'create' | 'edit' | 'delete'
  clusterId,
  pool,           // existing pool row (edit/delete)
  nodeclasses,    // array of available NodeClasses
  cloud,          // 'aws' | 'gcp'
  onClose,
  onSuccess,
}) => {
  const initial = useMemo(() => {
    if (mode === 'create') {
      return {
        name: '',
        instance_families: [],
        instance_categories: [],
        instance_sizes: [],
        capacity_types: ['on-demand'],
        architectures: ['amd64'],
        zones: [],
        labels: {},
        taints: [],
        limits_cpu: '',
        limits_memory: '',
        weight: null,
        disruption: null,
        node_class_ref: null,
      };
    }
    return projectPoolToForm(pool) || {};
  }, [mode, pool]);

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [forceDelete, setForceDelete] = useState(false);

  useEffect(() => { setForm(initial); setError(null); }, [initial]);

  const update = (patch) => setForm((s) => ({ ...s, ...patch }));
  const toggleInArray = (arr, val) => {
    const a = arr || [];
    return a.includes(val) ? a.filter((x) => x !== val) : [...a, val];
  };

  const handleSubmit = async () => {
    if (!clusterId) return;
    setSubmitting(true);
    setError(null);
    try {
      let url, method, body, name;
      if (mode === 'create') {
        if (!form.name) { setError('Name is required'); setSubmitting(false); return; }
        if (!form.node_class_ref) { setError('NodeClass is required'); setSubmitting(false); return; }
        if (!(form.capacity_types || []).length) {
          setError('Pick at least one capacity type'); setSubmitting(false); return;
        }
        url = `${API}/v1/clusters/${clusterId}/karpenter/nodepools`;
        method = 'post';
        body = form;
        name = form.name;
      } else if (mode === 'edit') {
        const patch = diffPayload(form, projectPoolToForm(pool) || {});
        if (Object.keys(patch).length === 0) {
          setError('No changes to apply');
          setSubmitting(false);
          return;
        }
        url = `${API}/v1/clusters/${clusterId}/karpenter/nodepools/${encodeURIComponent(pool.name)}`;
        method = 'put';
        body = patch;
        name = pool.name;
      } else if (mode === 'delete') {
        if (forceDelete && confirmName !== pool.name) {
          setError(`Type "${pool.name}" exactly to confirm force-delete`);
          setSubmitting(false);
          return;
        }
        url = `${API}/v1/clusters/${clusterId}/karpenter/nodepools/${encodeURIComponent(pool.name)}?force=${forceDelete}`;
        method = 'delete';
        body = null;
        name = pool.name;
      }
      const res = method === 'delete'
        ? await axios.delete(url)
        : await axios[method](url, body);
      onSuccess?.({ mode, pool_name: name, action_id: res.data?.action_id });
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' && 'Create NodePool'}
            {mode === 'edit' && `Edit NodePool '${pool?.name}'`}
            {mode === 'delete' && `Delete NodePool '${pool?.name}'`}
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
            {error}
          </div>
        )}

        {mode === 'delete' ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              This will delete the NodePool. With <strong>force=false</strong> the
              agent refuses if running NodeClaims still exist. With
              <strong> force=true</strong> the pool deletes immediately and Karpenter's
              GC reaps the NodeClaims (instances terminate).
            </p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={forceDelete}
                onChange={(e) => setForceDelete(e.target.checked)}
              />
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Force delete (allows running NodeClaims to be reaped)
            </label>
            {forceDelete && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Type the pool name to confirm:
                </label>
                <input
                  type="text"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                  placeholder={pool?.name}
                />
              </div>
            )}
            <p className="text-xs text-gray-500">
              Submitting creates a pending action — operator must approve in
              Node Recommendations to actually delete.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Identity */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name || ''}
                  disabled={mode === 'edit'}
                  onChange={(e) => update({ name: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded disabled:bg-gray-100"
                  placeholder="optops-test-pool"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  NodeClass {cloud === 'aws' ? '(EC2NodeClass)' : '(GCPNodeClass)'}
                </label>
                <select
                  value={form.node_class_ref?.name || ''}
                  onChange={(e) => {
                    const nc = (nodeclasses || []).find((n) => n.name === e.target.value);
                    if (!nc) return update({ node_class_ref: null });
                    update({
                      node_class_ref: {
                        group: cloud === 'aws' ? 'karpenter.k8s.aws' : 'karpenter.k8s.gcp',
                        kind: cloud === 'aws' ? 'EC2NodeClass' : 'GCPNodeClass',
                        name: nc.name,
                      },
                    });
                  }}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                >
                  <option value="">— pick one —</option>
                  {(nodeclasses || []).map((nc) => (
                    <option key={nc.name} value={nc.name}>{nc.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Capacity */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Capacity Types</label>
              <div className="flex gap-2">
                {CAPACITY_OPTIONS.map((c) => (
                  <label key={c} className="flex items-center gap-1 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(form.capacity_types || []).includes(c)}
                      onChange={() =>
                        update({ capacity_types: toggleInArray(form.capacity_types, c) })
                      }
                    />
                    {c}
                  </label>
                ))}
              </div>
            </div>

            {/* Architectures */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Architectures</label>
              <div className="flex gap-2">
                {ARCH_OPTIONS.map((a) => (
                  <label key={a} className="flex items-center gap-1 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(form.architectures || []).includes(a)}
                      onChange={() =>
                        update({ architectures: toggleInArray(form.architectures, a) })
                      }
                    />
                    {a}
                  </label>
                ))}
              </div>
            </div>

            {/* Scheduling chips */}
            <ChipsField
              label="Instance Families"
              hint="comma-separated (e.g. m5, c5, r5)"
              value={form.instance_families}
              onChange={(v) => update({ instance_families: v })}
            />
            <ChipsField
              label="Instance Categories"
              hint="alt to families (e.g. m, c, r)"
              value={form.instance_categories}
              onChange={(v) => update({ instance_categories: v })}
            />
            <ChipsField
              label="Instance Sizes"
              hint="optional (e.g. small, medium, large)"
              value={form.instance_sizes}
              onChange={(v) => update({ instance_sizes: v })}
            />
            <ChipsField
              label="Zones"
              hint="optional"
              value={form.zones}
              onChange={(v) => update({ zones: v })}
            />

            {/* Limits + Weight */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Limits CPU</label>
                <input
                  type="text"
                  value={form.limits_cpu || ''}
                  onChange={(e) => update({ limits_cpu: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                  placeholder="100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Limits Memory</label>
                <input
                  type="text"
                  value={form.limits_memory || ''}
                  onChange={(e) => update({ limits_memory: e.target.value })}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                  placeholder="200Gi"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Weight (0-100)</label>
                <input
                  type="number" min="0" max="100"
                  value={form.weight ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    update({ weight: v });
                  }}
                  className="w-full text-sm px-2 py-1.5 border rounded"
                />
              </div>
            </div>

            {/* Disruption */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-700">Disruption Policy</label>
                {!form.disruption && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => update({
                      disruption: {
                        consolidation_policy: 'WhenEmptyOrUnderutilized',
                        consolidate_after: DEFAULT_CONSOLIDATE_AFTER,
                        budgets: [],
                      },
                    })}
                  >
                    + Configure
                  </Button>
                )}
                {form.disruption && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => update({ disruption: null })}
                  >
                    Clear
                  </Button>
                )}
              </div>
              {form.disruption && (
                <DisruptionFields
                  value={form.disruption}
                  onChange={(v) => update({ disruption: v })}
                />
              )}
            </div>

            <p className="text-xs text-gray-500">
              Submitting creates a pending action. Operator must approve in
              Node Recommendations to actually
              {mode === 'create' ? ' create' : ' update'} the pool.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant={mode === 'delete' ? 'destructive' : 'default'}
          >
            {submitting
              ? 'Submitting…'
              : mode === 'create' ? 'Create' : mode === 'edit' ? 'Submit Update' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KarpenterPoolFormModal;
